import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { SurveysService } from '@app/services/api/surveys/surveys.service';
import { UserService } from '@app/services/api/user/user.service';
import { Observable, catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RedirectUserGuard implements CanActivate {
  public userData: any = null;
  public userSurvey: any = null;

  constructor(
    private router: Router,
    private userService: UserService,
    private surveyService: SurveysService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.initializeUserData().pipe(
      catchError(() => of(null)),
      switchMap(() => {
        return this.handleRedirectionBasedOnRoute(route);
      })
    );
  }

  initializeUserData(): Observable<any> {
    return forkJoin([
      this.getUserDetails(),
      this.getUserSurveyDetails(),
    ]).pipe(
      catchError(error => {
        console.error('Error in onboarding redirect guard:', error);
        return of([]);
      })
    );
  }

  getUserDetails(): Observable<any> {
    return this.userService.getMe().pipe(
      map(data => {
        this.userData = data;
        return data;
      })
    );
  }

  getUserSurveyDetails(): Observable<any> {
    return this.surveyService.getUserRiasecScore(environment.careerSurveyUuid).pipe(
      map(data => {
        this.userSurvey = data;
        return data;
      })
    );
  }

  handleRedirectionBasedOnRoute(route: ActivatedRouteSnapshot): Observable<boolean> {
    const currentRoute = route.url[0].path;
    const hasAcceptedTerms = this.checkIfUserAcceptedTerms();
    const hasSetUpProfile = this.checkIfUserSetUpProfile();
    const hasTakenSurvey = this.checkIfUserHasTakenSurvey();
    const isBiqRoute = currentRoute === 'user-info' || currentRoute.startsWith('user-info/');
    const isSurveyRoute = currentRoute === 'survey' || currentRoute.startsWith('survey/');

    // Redirection based on current route and user status
    if (currentRoute === 'terms') {
      if (hasAcceptedTerms) {
        // user has accepted terms, redirect to BIQ
        this.router.navigate(['/user-info']);
        return of(false);
      }
      return of(true);
    } else if (isBiqRoute) {
      if (hasSetUpProfile) {
        // user has set up profile, redirect to survey
        this.router.navigate(['/survey']);
        return of(false);
      } else if (!hasAcceptedTerms) {
        // user hasn't accepted terms, redirect to terms and conditions
        this.router.navigate(['/terms']);
        return of(false);
      }
      return of(true);
    } else if (isSurveyRoute) {
      if (hasTakenSurvey) {
        // user has taken survey, redirect to career matches
        this.router.navigate(['/career-matches']);
        return of(false);
      } else if (!hasAcceptedTerms) {
        // user hasn't accepted terms, redirect to terms and conditions
        this.router.navigate(['/terms']);
        return of(false);
      } else if (!hasSetUpProfile) {
        // user hasn't set up profile, redirect to BIQ
        this.router.navigate(['/user-info']);
        return of(false);
      }
      return of(true);
    } else {
      if (!hasAcceptedTerms) {
        // user hasn't accepted terms, redirect to terms and conditions
        this.router.navigate(['/terms']);
        return of(false);
      } else if (!hasSetUpProfile) {
        // user hasn't set up profile, redirect to BIQ
        this.router.navigate(['/user-info']);
        return of(false);
      } else if (!hasTakenSurvey) {
        // user hasn't taken survey, redirect to survey
        this.router.navigate(['/survey']);
        return of(false);
      }
      // If all conditions are met, allow navigation to the requested route
      return of(true);
    }
  }

  /**
   * Removed the automatic acceptance of terms as all the
   * existing users' records are saved in the db
   */
  checkIfUserAcceptedTerms(): boolean {
    return this.userData?.termsAccepted ? true : false;
  }

  checkIfUserSetUpProfile(): boolean {
    return this.userData.biq ? true : false;
  }

  checkIfUserHasTakenSurvey(): boolean {
    return this.userSurvey?.riasec_score ? true : false;
  }
}
